import "../templates-mobile/templates-mobile.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	checkCircle,
	edit_05,
	plusCircle,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";

import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";

const formatValue = (value: string | null | undefined): string =>
	value === null || value === undefined || value.trim() === "" ? "-" : value;

type TemplateMetricProps = {
	label: string;
	value: string;
};

const TemplateMetric = ({ label, value }: TemplateMetricProps) => (
	<div className={"templates-mobile-metric"}>
		<span className={"templates-mobile-metric-label"}>{label}</span>
		<span className={"templates-mobile-metric-value"}>{value}</span>
	</div>
);

type TemplateCardProps = {
	template: TemplateGridItem;
	isDefault: boolean;
	isBusy: boolean;
	onEdit: (templateId: number) => void;
	onDelete: (template: TemplateGridItem) => void;
	onToggleDefault: (templateId: number, shouldBeDefault: boolean) => void;
	/** When true the card is a selectable option (used by the picker modal). */
	selectable?: boolean;
	isSelected?: boolean;
	onToggleSelect?: (templateId: number) => void;
};

export const TemplateCard = ({
	template,
	isDefault,
	isBusy,
	onEdit,
	onDelete,
	onToggleDefault,
	selectable = false,
	isSelected = false,
	onToggleSelect,
}: TemplateCardProps) => {
	const details = (
		<>
			<div className={"templates-mobile-col"}>
				<div className={"templates-mobile-section"}>
					<span className={"templates-mobile-section-label"}>Pickup</span>
					<span className={"templates-mobile-section-name"}>
						{formatValue(template.shipperName)}
					</span>
					{template.pickupStreet ? (
						<span className={"templates-mobile-section-detail"}>
							{template.pickupStreet}
						</span>
					) : null}
					<span className={"templates-mobile-section-detail"}>
						{formatValue(template.pickupLocation)}
					</span>
				</div>
				<div className={"templates-mobile-section"}>
					<span className={"templates-mobile-section-label"}>Delivery</span>
					<span className={"templates-mobile-section-name"}>
						{formatValue(template.deliveryName)}
					</span>
					{template.deliveryStreet ? (
						<span className={"templates-mobile-section-detail"}>
							{template.deliveryStreet}
						</span>
					) : null}
					<span className={"templates-mobile-section-detail"}>
						{formatValue(template.deliveryLocation)}
					</span>
				</div>
			</div>

			<div className={"templates-mobile-col templates-mobile-col-right"}>
				<div className={"templates-mobile-section"}>
					<span className={"templates-mobile-section-label"}>Commodity</span>
					<span className={"templates-mobile-section-detail"}>
						{formatValue(template.commodity)}
					</span>
				</div>
				<div className={"templates-mobile-section"}>
					<span className={"templates-mobile-section-label"}>Account</span>
					<span className={"templates-mobile-section-detail"}>
						{formatValue(template.account)}
					</span>
				</div>
				<div className={"templates-mobile-metrics"}>
					<TemplateMetric label={"NMFC"} value={formatValue(template.nmfc)} />
					<TemplateMetric
						label={"Class"}
						value={formatValue(template.freightClass)}
					/>
					<TemplateMetric
						label={"Units"}
						value={formatValue(template.quantity)}
					/>
				</div>
			</div>
		</>
	);

	return (
		<article
			className={"templates-mobile-card"}
			data-template-id={template.templateId}
			data-testid={"template-card"}
		>
			<header className={"templates-mobile-card-header"}>
				<h2 className={"templates-mobile-name"}>
					{formatValue(template.templateName)}
				</h2>
				{isDefault ? (
					<span className={"templates-mobile-default-badge"}>Default</span>
				) : null}
				<div className={"templates-mobile-header-actions"}>
					<Button
						aria-label={isDefault ? "Remove default" : "Set as default"}
						data-testid={"template-set-default"}
						disabled={isBusy}
						onClick={() => onToggleDefault(template.templateId, !isDefault)}
						svgIcon={isDefault ? checkCircle : plusCircle}
						variant={"tertiary"}
					/>
					<Button
						aria-label={"Edit template"}
						data-testid={"template-edit"}
						disabled={isBusy}
						onClick={() => onEdit(template.templateId)}
						svgIcon={edit_05}
						variant={"tertiary"}
					/>
					<Button
						aria-label={"Delete template"}
						data-testid={"template-delete"}
						disabled={isBusy}
						onClick={() => onDelete(template)}
						svgIcon={trash_01}
						variant={"dangerTertiary"}
					/>
				</div>
			</header>

			{selectable ? (
				/* Tapping anywhere in the body toggles the visually hidden checkbox;
				   details stay readable and selection stays keyboard/AT accessible. */
				<label
					className={
						"templates-mobile-card-body templates-mobile-card-body-selectable"
					}
				>
					<input
						aria-label={`Select template ${formatValue(template.templateName)}`}
						checked={isSelected}
						className={"templates-mobile-select-input"}
						data-testid={"template-select"}
						disabled={isBusy}
						onChange={() => onToggleSelect?.(template.templateId)}
						type={"checkbox"}
					/>
					{details}
				</label>
			) : (
				<div className={"templates-mobile-card-body"}>{details}</div>
			)}
		</article>
	);
};
